import React, { useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal, notification } from "antd";
import { CheckCircleOutlined, HomeOutlined, LoadingOutlined, StopOutlined, WarningOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Form, Input, Select, Space, Tabs, Tooltip } from "antd";
import "./invoice_return_work_order.css";
import { IoAddCircleSharp, IoPrintSharp } from "react-icons/io5";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { HiOutlineDocumentCheck } from "react-icons/hi2";
import { FaPlusCircle } from "react-icons/fa";
import { TiDelete } from "react-icons/ti";
import { InvoiceReturnTable, InvoiceReturnOperationTable, useInvoiceReturnAutosave, useInvoiceReturnFormLogic, statusMapping } from "./hoc_invoice_return_work_order";
import ListItemInvoiceWorkOrder from "./list_item_invoice_work_order";
import axios from "axios";


interface ListItemType {
    id: React.Key;
    key: React.Key;
    type: string;
    job_type: string;
    item_no: string;
    description: string;
    qty: number;
    uom: string;
}

const InvoiceReturnWorkOrder: React.FC = () => {
    const tableRef = useRef<any>(null);
    const operationTableRef = useRef<any>(null);
    const itemModalRef = useRef<any>(null);
    const [form] = Form.useForm();
    const location = useLocation();
    const woNo = Form.useWatch('invoice_work_order_no', form);
    const returnNo = Form.useWatch('invoice_return_no', form);
    const [isLocked, setIsLocked] = useState(false);
    const getSparepartData = useCallback(() => tableRef.current?.getData() || [], []);
    const getOperationData = useCallback(() => operationTableRef.current?.getData() || [], []);
    const { autosaveStatus, triggerSave } = useInvoiceReturnAutosave(form, getSparepartData, getOperationData, isLocked);
    React.useEffect(() => {
        const currentReturnNo = form.getFieldValue('invoice_return_no');
        if (woNo && !currentReturnNo) {
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/initial-data`)
                .then(res => {
                    form.setFieldsValue({ invoice_return_no: res.data.invoice_return_no });
                    triggerSave();
                });
        } else if (!woNo) {
            form.setFieldsValue({ invoice_return_no: undefined });
        }
    }, [woNo, form, triggerSave]);
    const { fetchInitialData, handleNew, handleSubmit, handleVoid, handleAddItemsFromModal, loadData } = useInvoiceReturnFormLogic(
        form, tableRef, operationTableRef, triggerSave, setIsLocked
    );
     const handleSubmitWrapper = async () => {
        const spareparts = tableRef.current?.getData() || [];
        const hasOversupply = spareparts.some((item: any) => item.qtyReturn > item.qty);
        if (hasOversupply) {
            notification.warning({
                message: "Oversupply return!",
                description: "Cannot submit when oversupply warning exists."
            });
            return;
        }
        await handleSubmit();
    };
    React.useEffect(() => {
        const state = location.state as { invoice_return_no?: string };
        if (state?.invoice_return_no) {
            loadData(state.invoice_return_no);
        } else {
            fetchInitialData();
        }
    }, [location.state, loadData, fetchInitialData]);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const Printbtn = IoPrintSharp as React.ComponentType<any>;
    const Voidbtn = IoIosCloseCircleOutline as React.ComponentType<any>;
    const Submitbtn = HiOutlineDocumentCheck as React.ComponentType<any>;
    const Newbtn = FaPlusCircle as React.ComponentType<any>;
    const Addbtn = IoAddCircleSharp as React.ComponentType<any>;
    const Deletebtn = TiDelete as React.ComponentType<any>;


    const getCombinedExistingItemNos = () => {
        const spareparts = tableRef.current?.getData() || [];
        const operations = operationTableRef.current?.getData() || [];
        return [
            ...spareparts.map((item: any) => item.itemNo),
            ...operations.map((item: any) => item.operationNo)
        ];
    };

    const handleDeleteItems = () => { tableRef.current?.deleteSelectedItems(); operationTableRef.current?.deleteSelectedItems(); };


    return (
        <div id="is4l" style={transformStyle}>
            <div className="gjs-grid-row" id="ib7fh">
                <div className="gjs-grid-column" id="iczdv">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                href: "#/dashboard/data_invoice_return_work_order",
                                title: (
                                    <>
                                        <span>Data Invoice Return Work Order</span>
                                    </>
                                ),
                            },
                            {
                                title: "Invoice Return Work Order",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ixc32">
                <div className="gjs-grid-column" id="itrug">
                    <Button
                        className="gr-button"
                        onClick={handleNew}
                    >
                        <Newbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        New
                    </Button>
                    <Button
                        className="gr-button"
                        onClick={handleSubmitWrapper}
                        disabled={isLocked || autosaveStatus === 'processing'}
                    >
                        <Submitbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Submit
                    </Button>
                    <Button className="gr-button" onClick={handleVoid} disabled={!returnNo || isLocked || autosaveStatus === 'processing'}>
                        <Voidbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Void
                    </Button>
                    <Button className="gr-button">
                        <Printbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Print
                    </Button>
                    <Space style={{ marginLeft: '10px' }}>
                        {autosaveStatus === 'processing' && (
                            <Tooltip title="Autosave sedang berjalan...">
                                <LoadingOutlined style={{ color: '#1890ff' }} />
                            </Tooltip>
                        )}
                        {autosaveStatus === 'error' && (
                            <Tooltip title="Autosave bermasalah. Periksa konsol untuk detail.">
                                <WarningOutlined style={{ color: '#ff4d4f' }} />
                            </Tooltip>
                        )}
                        {autosaveStatus === 'stopped' && (
                            <Tooltip title="Autosave Stopped">
                                <StopOutlined style={{ color: '#bfbfbf' }} />
                            </Tooltip>
                        )}
                        {autosaveStatus === 'idle' && (
                            <Tooltip title="Autosave berhasil.">
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            </Tooltip>
                        )}
                    </Space>
                </div>
            </div>
            <div className="gjs-grid-row" id="ifmjf">
                <div className="gjs-grid-column" id="i5vit">
                    <Form
                        layout="horizontal"
                        form={form}
                        disabled={isLocked}
                        onValuesChange={triggerSave}
                        style={{ maxWidth: 450 }}
                        labelCol={{ style: { width: '190px', textAlign: "left" } }}
                        wrapperCol={{ style: { width: '290px' } }}
                        className="custom-info-purchase-request"
                    >
                        <Form.Item
                            label="Status"
                            name="status"
                            getValueProps={(value) => ({
                                value: statusMapping[value as keyof typeof statusMapping] || value
                            })}
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" readOnly />
                        </Form.Item>
                        <Form.Item
                            label="Invoice Return No"
                            name="invoice_return_no"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Date Return"
                            name="date_return"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Invoice Work Order No"
                            style={{ marginBottom: "5px" }} >
                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                <Form.Item
                                    name="invoice_work_order_no"
                                    noStyle
                                    style={{ width: "100%" }}
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Input
                                        style={{ width: "100%" }}
                                        readOnly
                                    />
                                </Form.Item>
                                <Button
                                    type="primary"
                                    id="list_data_work_order"
                                    disabled={!!woNo || isLocked}
                                    onClick={() => tableRef.current?.showModal()}
                                    icon={
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "40px", height: "20px" }}>
                                            <path d="M11.625 16.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" />
                                            <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Z" clipRule="evenodd" />
                                            <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
                                        </svg>}
                                />
                            </div>
                        </Form.Item>
                        <Form.Item
                            label="Work Order No"
                            name="work_order_no"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Warehouse"
                            name="warehouse"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Customer"
                            name="customer"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="No Polisi"
                            name="no_polisi"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Chassis No"
                            name="chassis_no"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Service Advisor"
                            name="service_advisor"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Bill To"
                            name="bill_to"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Remark"
                            name="remark"
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input.TextArea rows={3} placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Item Total"
                            name="item_total"
                            getValueProps={(value) => ({ value: value != null ? Math.round(Number(value)).toLocaleString('id-ID') : undefined })}
                            style={{
                                marginBottom: "5px",
                                paddingTop: "40px"
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Item Discount"
                            name="item_discount"
                            getValueProps={(value) => ({ value: value != null ? Math.round(Number(value)).toLocaleString('id-ID') : undefined })}
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Total"
                            name="total"
                            getValueProps={(value) => ({ value: value != null ? Math.round(Number(value)).toLocaleString('id-ID') : undefined })}
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Vat"
                            name="vat"
                            getValueProps={(value) => ({ value: value != null ? Math.round(Number(value)).toLocaleString('id-ID') : undefined })}
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                        <Form.Item
                            label="Grand Total"
                            name="grand_total"
                            getValueProps={(value) => ({ value: value != null ? Math.round(Number(value)).toLocaleString('id-ID') : undefined })}
                            style={{
                                marginBottom: "5px",
                            }}
                        >
                            <Input placeholder="" />
                        </Form.Item>
                    </Form>
                </div>
            </div>
            <div className="gjs-grid-row" id="ilp7b">
                <div className="gjs-grid-column" id="invvo">
                    <Button
                        className="gr-button"
                        onClick={() => itemModalRef.current?.showModal()}
                        disabled={isLocked || autosaveStatus === 'processing'}
                    >
                        <Addbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} id="list_item_work_order_return" />
                        Add Item
                    </Button>
                    <Button
                        className="gr-button"
                        onClick={handleDeleteItems}
                        disabled={isLocked || autosaveStatus === 'processing'}
                    >
                        <Deletebtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                        Delete
                    </Button>
                    <Tabs defaultActiveKey="spareparts" style={{ marginTop: '20px' }}> { }
                        <Tabs.TabPane tab="Spareparts" key="spareparts"> { }
                            <InvoiceReturnTable ref={tableRef} form={form} onDataChanged={triggerSave} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Operation" key="operation" forceRender>
                            <InvoiceReturnOperationTable ref={operationTableRef} form={form} onDataChanged={triggerSave} />
                        </Tabs.TabPane>
                    </Tabs>
                    <ListItemInvoiceWorkOrder ref={itemModalRef} form={form} onAddItems={handleAddItemsFromModal} existingItems={getCombinedExistingItemNos()} />
                </div>
            </div>
        </div>
    );
};
export default InvoiceReturnWorkOrder;